import { useForm } from "react-hook-form";
import { ICreateMarathon } from "features/createEditMarathon/model/types";
import {
  createMarathon,
  updateMarathon,
} from "../../../../entities/admin/model/thunk/createMarathon";
import { yupResolver } from "@hookform/resolvers/yup";
import { fillFormData, fillUpdateData } from "../converterToFormData";
import { toast } from "react-toastify";
import { useAppDispatch } from "shared/lib/hooks/hooks";
import { defaultValues } from "../defaultValues";
import { createValidationSchema } from "../validationSchema";
import {
  saveToLocal,
  loadFromLocal,
  removeFromLocal,
} from "shared/services/getLocal";
import { useEffect, useState } from "react";
import { convertFromRaw, EditorState } from "draft-js";
import { useOutletContext, useParams } from "react-router-dom";

type DateLike = Date | string | number | null | undefined;
type OutletCtx = {
  startDateAcceptingApplications: DateLike;
  endDateAcceptingApplications: DateLike;
};

const reviveEditorState = (text: any): EditorState => {
  try {
    if (text instanceof EditorState) return text;
    if (typeof text === "string") {
      const parsed = JSON.parse(text);
      return EditorState.createWithContent(convertFromRaw(parsed));
    }
    if (text?.blocks) {
      return EditorState.createWithContent(convertFromRaw(text));
    }
  } catch (e) {
    console.warn("Не удалось восстановить EditorState", e);
  }
  return EditorState.createEmpty();
};

const normalizeRawValues = (data: any): ICreateMarathon => {
  const plain = data instanceof Map ? Object.fromEntries(data.entries()) : data;

  return {
    ...plain,
    dateEvent: plain.date || plain.dateEvent,
    distances: plain.distances?.map((d: any) => {
      if (d.true && d.false) {
        return {
          ...d,
          true: d.true,
          false: d.false,
        };
      }

      if (d.distanceAges) {
        const result = {
          ...d,
          true: d.distanceAges.filter((da: any) => da.gender === true),
          false: d.distanceAges.filter((da: any) => da.gender === false),
        };
        return result;
      }

      return {
        ...d,
        true: [],
        false: [],
      };
    }),
    translations: plain.translations?.map((item: any) => {
      const result = {
        ...item,
        text: reviveEditorState(item.text),
        logo: item.logo,
      };
      return result;
    }),
    documents: plain.documents?.map((doc: any) => {
      if (typeof doc === "string") {
        return doc;
      } else if (doc?.document) {
        return doc.document;
      }
      return doc;
    }),
    partners: plain.partners,
    publicOffer:
      plain.offerAgreements?.map((offer: any) => ({
        languageId: offer.languageId,
        file: offer.documentPath,
      })) || plain.publicOffer,
  };
};

export const useMarathonForm = (
  setCreatedMarathonModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  isEditMode: boolean,
  data: ICreateMarathon
) => {
  const dispatch = useAppDispatch();
  const params = useParams();

  let initialData = data;
  if (!isEditMode) {
    const savedData = loadFromLocal("marathonFormData");
    if (savedData) {
      initialData = savedData as ICreateMarathon;
    }
  }

  const rawValues = isEditMode ? data : initialData || defaultValues;

  const defaultValuesPrepared: ICreateMarathon = normalizeRawValues(
    rawValues ?? defaultValues
  );

  const [originalData, setOriginalData] = useState<ICreateMarathon | null>(
    null
  );

  const form = useForm<ICreateMarathon>({
    defaultValues: defaultValuesPrepared,
    context: { index: 0 },
    resolver: yupResolver(
      createValidationSchema(
        isEditMode,
        data?.startDateAcceptingApplications,
        data?.endDateAcceptingApplications
      )
    ),
    mode: "all",
    shouldFocusError: true,
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (isEditMode && data) {
      const resetData = normalizeRawValues(data);
      form.reset(resetData);
      setOriginalData(data);
    }
  }, [isEditMode, data]);

  useEffect(() => {
    if (!isEditMode) {
      const subscription = form.watch((value) => {
        const dataToSave = {
          ...value,
          translations: value.translations?.map((translation: any) => ({
            ...translation,
            logo: null,
            text: null,
          })),
          documents: value.documents?.map(() => null),
          partners: value.partners?.map((partner: any) => ({
            ...partner,
            partnerCompanies: partner.partnerCompanies?.map(() => null),
          })),
          publicOffer: value.publicOffer?.map((offer: any) => ({
            ...offer,
            file: null,
          })),
        };
        saveToLocal("marathonFormData", dataToSave);
      });
      return () => subscription.unsubscribe();
    }
  }, [form, isEditMode]);

  const handleCreateMarathon = async (data: ICreateMarathon) => {
    const hasDistancePrices = data.distances?.some(
      (distance) => distance.distancePrices?.length > 0
    );

    if (!hasDistancePrices) {
      form.setError("root", {
        type: "manual",
        message: "Хотя бы 1 период регистрации должен быть открыт",
      });
      return;
    }

    const createdMarathonResponse = await dispatch(
      // @ts-ignore
      createMarathon(fillFormData(data))
    );

    if (createdMarathonResponse?.meta?.requestStatus === "fulfilled") {
      setCreatedMarathonModalOpen(true);
      removeFromLocal("marathonFormData");
      form.reset(defaultValues);
    }
    if (createdMarathonResponse?.meta?.requestStatus === "rejected") {
      toast.error("Вы заполнили не все поля. Пожалуйста, проверьте");
    }
  };

  const handleUpdateMarathon = async (data: ICreateMarathon) => {
    const hasDistancePrices = data.distances?.some(
      (distance) => distance.distancePrices?.length > 0
    );

    if (!hasDistancePrices) {
      form.setError("root", {
        type: "manual",
        message: "Хотя бы 1 период регистрации должен быть открыт",
      });
      return;
    }

    const mergedData = {
      ...data,
      translations: data.translations?.map(
        (translation: any, index: number) => ({
          ...translation,
          id: (originalData?.translations as any)?.[index]?.id,
        })
      ),
      partners: data.partners?.map((partner: any, partnerIndex: number) => ({
        ...partner,
        id: (originalData?.partners as any)?.[partnerIndex]?.id,
        translations: partner.translations?.map(
          (translation: any, transIndex: number) => ({
            ...translation,
            id: (originalData?.partners as any)?.[partnerIndex]?.translations?.[
              transIndex
            ]?.id,
          })
        ),
        partnerCompanies: partner.partnerCompanies?.map(
          (company: any, companyIndex: number) => ({
            ...company,
            id: (originalData?.partners as any)?.[partnerIndex]
              ?.partnerCompanies?.[companyIndex]?.id,
          })
        ),
      })),
      distances: data.distances?.map(
        (distance: any, distanceIndex: number) => ({
          ...distance,
          id: (originalData?.distances as any)?.[distanceIndex]?.id,
          startNumberRanges: distance.startNumberRanges?.map(
            (range: any, rangeIndex: number) => ({
              ...range,
              id: (originalData?.distances as any)?.[distanceIndex]
                ?.startNumberRanges?.[rangeIndex]?.id,
            })
          ),
          distancePrices: distance.distancePrices?.map(
            (price: any, priceIndex: number) => ({
              ...price,
              id: (originalData?.distances as any)?.[distanceIndex]
                ?.distancePrices?.[priceIndex]?.id,
            })
          ),
          true: distance.true?.map((age: any, ageIndex: number) => ({
            ...age,
            id: (originalData?.distances as any)?.[
              distanceIndex
            ]?.distanceAges?.filter((a: any) => a.gender === true)?.[ageIndex]
              ?.id,
          })),
          false: distance.false?.map((age: any, ageIndex: number) => ({
            ...age,
            id: (originalData?.distances as any)?.[
              distanceIndex
            ]?.distanceAges?.filter((a: any) => a.gender === false)?.[ageIndex]
              ?.id,
          })),
        })
      ),
    };

    const updateData = await fillUpdateData({ ...mergedData, id: params.id });

    const updatedMarathonResponse = await dispatch(
      // @ts-ignore
      updateMarathon(updateData)
    );

    if (updatedMarathonResponse?.meta?.requestStatus === "fulfilled") {
      toast.success("Марафон успешно обновлен!");
    }
    if (updatedMarathonResponse?.meta?.requestStatus === "rejected") {
      toast.error(
        "Ошибка при обновлении марафона. Пожалуйста, проверьте данные"
      );
    }
  };

  return {
    handleCreateMarathon,
    handleUpdateMarathon,
    form,
  };
};
